import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { INR, StatusBadge } from "@/lib/format";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClipboardList, Loader2, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ContactButtons } from "@/components/ContactButtons";

function Column({ title, items, tone, render }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm">{title}</h3>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tone}`}>{items.length}</span>
      </div>
      <div className="space-y-2 max-h-[440px] overflow-y-auto">
        {items.length === 0 ? <p className="text-xs text-muted-foreground py-6 text-center">Nothing here</p> : items.map(render)}
      </div>
    </Card>
  );
}

export default function LoanFollowups() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/loan/followups").then(({ data }) => setD(data)).catch(() => setD(null)); }, []);
  if (!d) return <div className="flex justify-center h-96 items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const appCard = (a) => (
    <div key={a.id} className="rounded-lg border border-border p-2.5 text-sm" data-testid="loan-followup-item">
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs">{a.loan_app_id}</span><StatusBadge value={a.status} />
        {a.customer_id && <Button size="icon" variant="ghost" className="h-6 w-6 ml-auto" onClick={() => nav(`/customers/${a.customer_id}`)}><Eye className="h-3.5 w-3.5" /></Button>}
      </div>
      <div className="font-semibold mt-1">{a.customer_name}</div>
      <div className="flex items-center justify-between"><div className="text-xs text-muted-foreground">{a.loan_product} · {a.lender} · {INR(a.requested_amount)}</div>
        <ContactButtons name={a.customer_name} mobile={a.customer_mobile} product={a.loan_product} testPrefix="followup" track={{ customer_id: a.customer_id, loan_app_id: a.id, business: "loan", context: "loan_followup" }} /></div>
    </div>
  );
  const commCard = (c) => (
    <div key={c.id} className="rounded-lg border border-border p-2.5 text-sm" data-testid="loan-followup-item">
      <div className="flex items-center gap-2"><span className="font-mono text-xs">{c.loan_app_number}</span><StatusBadge value={c.payment_status} /></div>
      <div className="font-semibold mt-1">{c.customer_name}</div>
      <div className="text-xs text-muted-foreground">{c.lender} · pending {INR(c.commission_pending)}</div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Loan Follow-up Queue" subtitle="Daily working board for the loan team" icon={ClipboardList} />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Column title="Overdue Follow-ups" items={d.overdue} tone="bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300" render={appCard} />
        <Column title="Documents Pending" items={d.docs_pending} tone="bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" render={appCard} />
        <Column title="With Lender" items={d.with_lender} tone="bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300" render={appCard} />
        <Column title="Approved — Not Disbursed" items={d.approved_not_disbursed} tone="bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300" render={appCard} />
        <Column title="Commission Pending" items={d.commission_pending} tone="bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" render={commCard} />
      </div>
    </div>
  );
}
